import React, { useEffect } from 'react';
import { Switch, Route, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';

import PublicLayout from '../layouts/public';
import { useUserDataContext } from '../wiring/user-provider';
import slugify from '../utils/slugify';

const SITE_URL = 'https://www.readthefingdirections.com';
const DEFAULT_TITLE = 'Read the F*ing Directions - Spread the Vote';
const DEFAULT_DESCRIPTION =
    'Want to vote by mail? Make sure your ballot is counted. State-by-state instructions, deadlines, eligibility, absentee ballot rules, how to request a ballot, where to buy a stamp, where to find a notary, and how to submit your ballot.';
const DEFAULT_URL = `${SITE_URL}/`;

const App = () => {
    const location = useLocation();
    const { selectedState } = useUserDataContext();

    // public/index.html ships default title/description/canonical/og/twitter
    // tags as a fallback for crawlers that don't run JS. Once React mounts,
    // Helmet takes over managing those same tags below, so the static ones
    // become redundant duplicates (Helmet doesn't know about tags it didn't
    // create) and are removed here to avoid two conflicting copies existing
    // at once for anything that does execute JS.
    useEffect(() => {
        document
            .querySelectorAll('[data-default]')
            .forEach((el) => el.remove());
    }, []);

    const title = selectedState
        ? `Vote by Mail in ${selectedState} | Read the F*ing Directions`
        : DEFAULT_TITLE;
    const description = selectedState
        ? `Everything you need to vote by mail in ${selectedState}: eligibility, deadlines, and step-by-step instructions.`
        : DEFAULT_DESCRIPTION;
    const canonicalUrl = selectedState
        ? `${SITE_URL}/state/${slugify(selectedState)}`
        : DEFAULT_URL;

    // GA's default `config` pageview only fires once, on initial script load
    // (disabled via send_page_view: false in public/index.html) — since this
    // is a client-side-routed SPA, each navigation needs its own manual event.
    // Confirmed via testing that GA4's automatic history-based tracking does
    // NOT pick these up on its own, so this manual call is required, not
    // just a defensive fallback. Fired from here (rather than history.listen
    // in src/index.js) so it can use `title` as computed above instead of
    // document.title, which Helmet updates asynchronously and wouldn't yet
    // reflect the new page at the moment a history listener fires.
    useEffect(() => {
        if (typeof window.gtag === 'function') {
            window.gtag('event', 'page_view', {
                page_path: location.pathname + location.search,
                page_location: window.location.href,
                page_title: title,
            });
        }
    }, [location.pathname, location.search, title]);

    return (
        <>
            <Helmet>
                <title>{title}</title>
                <meta name="description" content={description} />
                <link rel="canonical" href={canonicalUrl} />
                <meta property="og:title" content={title} />
                <meta property="og:description" content={description} />
                <meta property="og:url" content={canonicalUrl} />
                <meta name="twitter:title" content={title} />
                <meta name="twitter:description" content={description} />
            </Helmet>
            <Switch location={location}>
                <Route path="/state/:stateSlug">
                    <PublicLayout />
                </Route>
                <Route path="/">
                    <PublicLayout />
                </Route>
            </Switch>
        </>
    );
};

export default App;
